/**
 * Drop-in trigger + dialog for the unified school print — the one component
 * every school surface mounts in its header so the print affordance (and the
 * dialog behind it) cannot drift between Production Records, History, Serve,
 * and Today.
 */

import { Button } from '@ordo/ui/button'
import { toast } from '@ordo/ui/sonner'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ordo/ui/tooltip'
import { usePrintAction } from '@ordo/ui/use-print-action'
import { Printer } from 'lucide-react'
import { useState } from 'react'

import { SchoolPrintDialog } from '@/components/school-print/school-print-dialog'
import type { SchoolPrintSections } from '@/components/school-print/use-school-print-preview'

export function SchoolPrintButton({
  locationId,
  hideRecordsSection,
  defaultSections,
  label = 'Print',
  size = 'sm',
  variant = 'outline',
  campuses,
  disabledReason,
}: {
  locationId: string | null
  hideRecordsSection?: boolean
  /** Per-surface starting selection (see `useSchoolPrintPreview`). */
  defaultSections?: Partial<SchoolPrintSections>
  /** A multi-campus school's campuses: the dialog then asks which one to print (see `SchoolPrintDialog`). */
  campuses?: ReadonlyArray<{ id: string; name: string }>
  /** Trigger label; the dialog behind it is the same everywhere. */
  label?: string
  size?: 'sm' | 'default'
  variant?: 'outline' | 'default' | 'ghost'
  /**
   * Why there is nothing to print here (Serve Now on a day with no service,
   * PROD-1472). Set, the button is disabled with this as its tooltip and
   * Cmd/Ctrl+P is left to the browser. One prop, so the button can never be
   * disabled without saying why.
   */
  disabledReason?: string
}) {
  const [open, setOpen] = useState(false)

  // Cmd/Ctrl+P opens the unified print dialog instead of printing the raw
  // screen — every surface that mounts this button gets the shortcut for
  // free. A print from the browser menu / mobile share sheet can't be
  // intercepted, so after one runs we open the dialog and say why the
  // printout wasn't the packet.
  usePrintAction({
    enabled: Boolean(locationId) && !disabledReason,
    onPrintRequest: () => setOpen(true),
    onUnhandledSystemPrint: () => {
      setOpen(true)
      toast.info('To print school packets, use Print — pick the days and sections here.')
    },
  })

  if (!locationId) {
    return null
  }
  const trigger = (
    <Button
      type="button"
      size={size}
      variant={variant}
      disabled={Boolean(disabledReason)}
      onClick={() => setOpen(true)}>
      <Printer aria-hidden />
      {label}
    </Button>
  )
  return (
    <>
      {disabledReason ? (
        // A disabled button fires no pointer events, so the tooltip hangs off a
        // focusable wrapper: hover and keyboard focus both say why.
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex">
                {trigger}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">{disabledReason}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        trigger
      )}
      <SchoolPrintDialog
        open={open}
        onOpenChange={setOpen}
        locationId={locationId}
        hideRecordsSection={hideRecordsSection}
        defaultSections={defaultSections}
        campuses={campuses}
      />
    </>
  )
}
